import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import heroBackdrop from '../assets/pixe.png'
import heroLogo from '../assets/pixeloop-logo.png'
import { setToken } from '../lib/auth'


export default function Login() {
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const formRef = useRef<HTMLFormElement | null>(null)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  function resolveReturnPath(): string {
    const raw = searchParams.get('return_to') || searchParams.get('next') || '/'
    const decoded = (() => {
      try {
        return decodeURIComponent(raw)
      } catch {
        return raw
      }
    })()
    if (!decoded.startsWith('/')) return '/'
    return decoded
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const raw = username.trim()
      const u1 = raw.startsWith('@') ? raw.slice(1) : raw
      let res = await fetch('/api/auth/token/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u1, password }),
      })
      if (res.status === 401 && u1.includes('@')) {
        const local = u1.split('@', 1)[0]
        if (local) {
          res = await fetch('/api/auth/token/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: local, password }),
          })
        }
      }
      if (!res.ok) {
        if (res.status === 401) throw new Error('Invalid username or password')
        throw new Error(`HTTP ${res.status}`)
      }
      const data = await res.json()
      if (!data?.access) throw new Error('Missing access token')
      setToken(data.access)
      if (data.refresh) localStorage.setItem('refresh', data.refresh)
      nav(resolveReturnPath())
    } catch (e: any) {
      setError(e?.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  function openSupport() {
    window.location.href = 'mailto:support@pixeloop.com?subject=PixeLoop%20Reset'
  }

  function handleKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key !== 'Enter' || loading) return
    const target = e.target as HTMLElement | null
    if (target && target.tagName === 'TEXTAREA') return
    e.preventDefault()
    if (formRef.current) {
      try {
        formRef.current.requestSubmit()
      } catch {
        formRef.current.submit()
      }
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#0b0b0b] via-[#070707] to-black text-white">
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <img src={heroBackdrop} alt="" className="h-full w-full object-cover blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(255,0,122,0.25)] to-[rgba(0,122,255,0.25)]" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col justify-center gap-10 px-6 py-12 lg:flex-row lg:items-center">
        <div className="flex-1 text-center lg:text-left">
          <Link
            to="/"
            aria-label="PixeLoop home"
            className="mb-4 inline-flex items-center justify-center lg:justify-start"
          >
            <img src={heroLogo} alt="PixeLoop" className="h-14 w-auto drop-shadow-[0_10px_25px_rgba(0,0,0,0.55)]" />
          </Link>
          <h1 className="mt-8 text-3xl font-black md:text-4xl">Welcome back to your Loop</h1>
          <p className="mt-3 text-base text-white/75 md:text-lg">
            Sign in to publish, read, shop, and mint handles across the community. Secure auth, on-chain handles, and
            commerce in one dashboard.
          </p>
          <div className="mt-8 flex flex-col gap-3 text-left">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-white/50">Security</p>
              <p className="mt-2 text-sm text-white/80">Encrypted auth tokens. We never share your credentials.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-white/50">New here?</p>
              <p className="mt-2 text-sm text-white/80">
                Create an account and start onboarding in minutes.
              </p>
              <Link
                to="/signup"
                className="mt-3 inline-flex items-center justify-center rounded-full bg-[#1d4ed8] px-5 py-2 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(0,0,0,0.35)] hover:bg-[#1a46c2]"
              >
                Create account
              </Link>
            </div>
          </div>
        </div>

        <form
          ref={formRef}
          onSubmit={onSubmit}
          onKeyDown={handleKeyDown}
          className="w-full max-w-md rounded-[30px] border border-white/15 bg-black/60 p-7 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur"
        >
          <h2 className="text-2xl font-black">Sign in</h2>
          <p className="mt-2 text-sm text-white/80">Use your username or email plus password.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold text-white/80">Username or email</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="@handle or you@example.com"
                className="mt-2 w-full rounded-2xl border border-white/15 bg-[#1a1921] px-4 py-3 text-base text-white placeholder:text-white/70 outline-none focus:border-white focus:ring-2 focus:ring-pink-400/60"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-white/80">Password</label>
              <div className="mt-2 flex items-center rounded-2xl border border-white/15 bg-[#1a1921] px-4">
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  type={showPw ? 'text' : 'password'}
                  className="flex-1 bg-transparent py-3 text-base text-white placeholder:text-white/65 outline-none"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="px-2 text-sm font-semibold text-white/70 hover:text-white"
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
              <button
                type="button"
                onClick={openSupport}
                className="mt-2 text-xs font-semibold text-white/70 hover:text-white"
              >
                Forgot password?
              </button>
            </div>
          </div>

          {error && <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full rounded-2xl bg-[#1d4ed8] px-6 py-3 text-base font-bold text-white shadow-[0_15px_35px_rgba(0,0,0,0.45)] transition hover:bg-[#1a46c2] disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-white/80">
            Need an account?{' '}
            <Link to="/signup" className="font-semibold text-white hover:underline">
              Start onboarding
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
